import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useMaintenanceCheck } from "@/hooks/useMaintenanceCheck";
import Index from "./pages/Index";
import LandingOption1 from "./pages/LandingOption1";
import LandingOption2 from "./pages/LandingOption2";
import LandingOption3 from "./pages/LandingOption3";
import LandingOption4 from "./pages/LandingOption4";
import LandingOption5 from "./pages/LandingOption5";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import About from "./pages/About";
import Features from "./pages/Features";
import Contact from "./pages/Contact";
import Dashboard from "./pages/Dashboard";
import ChairDashboard from "./pages/ChairDashboard";
import TreasurerDashboard from "./pages/TreasurerDashboard";
import SecretaryDashboard from "./pages/SecretaryDashboard";
import TrusteeDashboard from "./pages/TrusteeDashboard";
import AssetsDashboard from "./pages/AssetsDashboard";
import CateringDashboard from "./pages/CateringDashboard";
import EventsDashboard from "./pages/EventsDashboard";
import CommunicationsDashboard from "./pages/CommunicationsDashboard";
import CommunityDashboard from "./pages/CommunityDashboard";
import SchoolLiaisonDashboard from "./pages/SchoolLiaisonDashboard";
import ShopDashboard from "./pages/ShopDashboard";
import Settings from "./pages/Settings";
import Library from "./pages/Library";
import Forum from "./pages/Forum";
import Shop from "./pages/Shop";
import Marketplace from "./pages/Marketplace";
import CreateListing from "./pages/CreateListing";
import BuyCredits from "./pages/BuyCredits";
import MyListings from "./pages/MyListings";
import FundraisingPage from "./pages/FundraisingPage";
import Documents from "./pages/Documents";
import Members from "./pages/Members";
import Pricing from "./pages/Pricing";
import PricingNew from "./pages/PricingNew";
import Support from "./pages/Support";
import Games from "./pages/Games";
import RaffleGame from "./pages/RaffleGame";
import GuessingGame from "./pages/GuessingGame";
import BingoGame from "./pages/BingoGame";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import Refund from "./pages/Refund";
import NotFound from "./pages/NotFound";
import Maintenance from "./pages/Maintenance";
import AdminLogin from "./pages/AdminLogin";
import PTAAdmin from "./pages/PTAAdmin";
import SiteAdmin from "./pages/SiteAdmin";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import StaffSubmission from "./pages/StaffSubmission";
import Changelog from "./pages/Changelog";
import ParentPortal from "./pages/ParentPortal";
import ParentProfile from "./pages/ParentProfile";
import Sponsorship from "./pages/Sponsorship";
import Integrations from "./pages/Integrations";
import Partners from "./pages/Partners";
import StallApplication from "./pages/StallApplication";
import TrialExpired from "./pages/TrialExpired";
import AcceptInvitation from "./pages/AcceptInvitation";
import BecomeAMember from "./pages/BecomeAMember";
import LoanPayment from "./pages/LoanPayment";
import StallPayment from "./pages/StallPayment";
import VolunteerSignup from "./pages/VolunteerSignup";
import EventCheckin from "./pages/EventCheckin";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyParent from "./pages/VerifyParent";
import CheckEmail from "./pages/CheckEmail";
import CheckParentEmail from "./pages/CheckParentEmail";

const queryClient = new QueryClient();

const Layout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-screen flex-col">
    <Header />
    <main className="flex-1 container py-6">
      {children}
    </main>
    <Footer />
    <CookieBanner />
  </div>
);

const App = () => {
  const { showMaintenance, loading } = useMaintenanceCheck();

  if (loading) {
    return null; // or a loading spinner
  }

  if (showMaintenance) {
    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="*" element={<Maintenance />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
        <Route path="/" element={<Layout><Index /></Layout>} />
        <Route path="/landing-option-1" element={<Layout><LandingOption1 /></Layout>} />
        <Route path="/landing-option-2" element={<Layout><LandingOption2 /></Layout>} />
        <Route path="/landing-option-3" element={<Layout><LandingOption3 /></Layout>} />
        <Route path="/landing-option-4" element={<Layout><LandingOption4 /></Layout>} />
        <Route path="/landing-option-5" element={<Layout><LandingOption5 /></Layout>} />
          <Route path="/auth" element={<Layout><Auth /></Layout>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-parent" element={<VerifyParent />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/check-parent-email" element={<CheckParentEmail />} />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/about" element={<Layout><About /></Layout>} />
        <Route path="/features" element={<Layout><Features /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/chair" element={<ProtectedRoute><Layout><ChairDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/treasurer" element={<ProtectedRoute><Layout><TreasurerDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/secretary" element={<ProtectedRoute><Layout><SecretaryDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/trustee" element={<ProtectedRoute><Layout><TrusteeDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/assets" element={<ProtectedRoute><Layout><AssetsDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/catering" element={<ProtectedRoute><Layout><CateringDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/events" element={<ProtectedRoute><Layout><EventsDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/communications" element={<ProtectedRoute><Layout><CommunicationsDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/community" element={<ProtectedRoute><Layout><CommunityDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/school-liaison" element={<ProtectedRoute><Layout><SchoolLiaisonDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/shop" element={<ProtectedRoute><Layout><ShopDashboard /></Layout></ProtectedRoute>} />
        <Route path="/pta-admin" element={<ProtectedRoute><Layout><PTAAdmin /></Layout></ProtectedRoute>} />
        <Route path="/trial-expired" element={<ProtectedRoute><Layout><TrialExpired /></Layout></ProtectedRoute>} />
        <Route path="/site-admin" element={<ProtectedRoute requireRole="site_admin"><Layout><SiteAdmin /></Layout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><Layout><Library /></Layout></ProtectedRoute>} />
        <Route path="/forum" element={<ProtectedRoute><Layout><Forum /></Layout></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><Layout><Shop /></Layout></ProtectedRoute>} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/create" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/marketplace/buy-credits" element={<ProtectedRoute><BuyCredits /></ProtectedRoute>} />
        <Route path="/marketplace/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />
        <Route path="/fundraising" element={<FundraisingPage />} />
        <Route path="/documents" element={<ProtectedRoute><Layout><Documents /></Layout></ProtectedRoute>} />
        <Route path="/members" element={<ProtectedRoute><Layout><Members /></Layout></ProtectedRoute>} />
        <Route path="/pricing" element={<Layout><PricingNew /></Layout>} />
        <Route path="/pricing-old" element={<Layout><Pricing /></Layout>} />
        <Route path="/support" element={<Layout><Support /></Layout>} />
        <Route path="/games" element={<Layout><Games /></Layout>} />
        <Route path="/games/raffle" element={<Layout><RaffleGame /></Layout>} />
        <Route path="/games/guessing" element={<Layout><GuessingGame /></Layout>} />
        <Route path="/games/bingo" element={<Layout><BingoGame /></Layout>} />
        <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
        <Route path="/terms" element={<Layout><Terms /></Layout>} />
        <Route path="/cookies" element={<Layout><Cookies /></Layout>} />
        <Route path="/refund" element={<Layout><Refund /></Layout>} />
        <Route path="/blog" element={<Layout><Blog /></Layout>} />
        <Route path="/blog/:id" element={<Layout><BlogPost /></Layout>} />
        <Route path="/changelog" element={<Layout><Changelog /></Layout>} />
        <Route path="/portal" element={<ParentPortal />} />
        <Route path="/portal/:ptaId" element={<ParentPortal />} />
        <Route path="/parent-profile" element={<ParentProfile />} />
        <Route path="/sponsorship" element={<Layout><Sponsorship /></Layout>} />
        <Route path="/integrations" element={<Layout><Integrations /></Layout>} />
        <Route path="/partners" element={<Layout><Partners /></Layout>} />
        <Route path="/staff-request/:ptaId" element={<StaffSubmission />} />
        <Route path="/stall-application/:ptaId" element={<StallApplication />} />
        <Route path="/fundraising/:ptaId" element={<FundraisingPage />} />
        <Route path="/become-a-member/:ptaId" element={<BecomeAMember />} />
        <Route path="/loan-payment/:loanId" element={<LoanPayment />} />
        <Route path="/stall-payment/:applicationId" element={<StallPayment />} />
        <Route path="/stalls/:ptaId" element={<StallApplication />} />
        <Route path="/v/:ptaId" element={<VolunteerSignup />} />
        <Route path="/event-checkin" element={<ProtectedRoute><EventCheckin /></ProtectedRoute>} />
        <Route path="/event-checkin/:eventId" element={<ProtectedRoute><EventCheckin /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </QueryClientProvider>
);
};

export default App;
